import { Number, Skeleton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { duration } from "common";
import React from "react";
import { useQuery } from "react-query";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import {
    isAmountIncludesPartnerFee,
    isFlexLevelPaidByTraveler,
    isPaidBySeller,
} from "../../../models/PartnerFeeFormula";
import { getIncomeReport, IncomeReport } from "../../../reports/IncomeReport";
import { useAnalyticsFilterStore } from "../../../stores/reports/analyticsFilterStore";
import { numberFormat } from "../../../utils/numbers";
import { Currency } from "../../Common/Currency";

export const IncomeStats = () => {
    const seller = useSeller();

    const [from, to] = useAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useAnalyticsFilterStore((state) => state.filters);
    const productsList: ID[] = filters.products.all ? [] : filters.products.selected ?? [];

    const params = { seller, mode: filters.mode, products: productsList, from, to };
    const { data, isLoading } = useQuery<IncomeReport>(["incomeStats", params], async () => getIncomeReport(params), {
        staleTime: duration(3, "minutes"),
    });

    if (isLoading || !data) {
        return <Skeleton height={115} />;
    }

    const includesPartnerFee = isAmountIncludesPartnerFee(seller.partnerFeeFormulas);
    const showGuestFees = isFlexLevelPaidByTraveler(seller) && includesPartnerFee;
    const showPartnerFee = isPaidBySeller(seller.partnerFeeFormulas) || includesPartnerFee;

    return (
        <div className="flex w-full flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-2">
            <div>
                <IncomeTile title="Income" amount={data.income} className="flex w-full" />
            </div>
            <span className="w-full truncate lg:flex-grow">
                <div className="flex flex-wrap justify-between rounded border border-gray-light">
                    <Tile title="Transactions" value={data.transactionCount} isCurrency={false} />
                    <Tile title="Charges" value={data.charges} />
                    <Tile title="Refunds" value={data.refunds} />
                    <Tile title="Collections" value={data.collections} />
                    <Tile title="CC Fees" value={data.processingFees} />
                    {showGuestFees && <Tile title="Guest Fees" value={data.guestFees} />}
                    {showPartnerFee && <Tile title="Service Fees" value={data.partnerFees} />}
                    <Tile title="Taxes &amp; Fees" value={data.taxes} />
                </div>
            </span>
        </div>
    );
};

interface IncomeTileProps {
    title: string;
    amount: number;
    className: string;
}

const IncomeTile = ({ title, amount, className }: IncomeTileProps) => {
    return (
        <Tooltip content={`Income after taxes, fees & refunds: ${numberFormat(amount)}`}>
            <div
                className={clsx(
                    "flex h-28 cursor-default flex-col items-center justify-center rounded bg-green-lighter p-4 xl:p-6",
                    className,
                )}
            >
                <div className="font-bold">{title}</div>
                <div className="whitespace-nowrap text-2xl font-bold">
                    <Currency compact>{amount}</Currency>
                </div>
            </div>
        </Tooltip>
    );
};

const Tile = ({ title, value, isCurrency = true }: { title: string; value: number; isCurrency?: boolean }) => {
    return (
        <Tooltip content={numberFormat(value)}>
            <div className="flex h-28 cursor-default flex-col items-center justify-center whitespace-nowrap rounded p-4 xl:p-6">
                <div>{title}</div>
                <div className="text-md font-bold">
                    {isCurrency ? (
                        <Currency compact>{value ?? 0}</Currency>
                    ) : (
                        <Number compact maximumFractionDigits={0}>
                            {value}
                        </Number>
                    )}
                </div>
            </div>
        </Tooltip>
    );
};
